import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, FileText, Download, Upload, Loader2, ImagePlus, FolderClosed, ExternalLink, WifiOff, ZoomIn, ZoomOut, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = process.env.REACT_APP_BACKEND_URL;
const TT = (lang, en, es) => (lang === 'es' ? es : en);

// Format an ISO or free-text revision date for display without altering stored value.
const fmtRev = (v, lang) => {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return v;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const fileUrl = (id, l, token, dl) => `${API}/api/team/sds/${id}/file?lang=${l}&auth=${encodeURIComponent(token)}${dl ? '&dl=1' : ''}`;
const photoUrl = (id, token, v) => `${API}/api/team/sds/${id}/photo?variant=card&auth=${encodeURIComponent(token)}${v ? `&v=${encodeURIComponent(v)}` : ''}`;

// Display-only naming. All four products are Spartan "Clean on the Go" dispensing
// concentrates; the card shows the short primary name with the shared classification
// beneath. The underlying product_name / product_code in the database are never changed.
const CLASSIFICATION = 'Clean on the Go';
const primaryName = (name) => ((name || '').replace(/^Clean on the Go\s+/i, '').trim() || name || '');

// ---------------- PDF viewer (PDF.js canvas — reliable in all browsers) ----------------
const Viewer = ({ row, lang, token, onClose }) => {
  const [vl, setVl] = useState(lang);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [width, setWidth] = useState(640);
  const [err, setErr] = useState(false);
  const wrapRef = useRef(null);
  const rev = vl === 'es' ? row.es_revision : row.en_revision;
  const available = vl === 'es' ? row.has_es : row.has_en;

  useEffect(() => { setPage(1); setErr(false); }, [vl]);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWidth(Math.max(260, el.clientWidth - 24)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Fetch with the auth query param via react-pdf's loader.
  const fileProp = useMemo(() => (available ? fileUrl(row.id, vl, token) : null), [available, row.id, vl, token]);
  const docOptions = useMemo(() => ({}), []);
  const safePage = numPages ? Math.min(page, numPages) : page;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex h-[92vh] max-w-4xl flex-col gap-0 overflow-hidden p-0" data-testid="sds-viewer">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-extrabold text-[#0E2A47]" data-testid="sds-viewer-title">{primaryName(row.product_name)}</div>
            <div className="text-[11px] text-slate-500">{CLASSIFICATION} · #{row.product_code}{rev ? ` · ${TT(vl, 'Revision', 'Revisión')}: ${fmtRev(rev, vl)}` : ''}</div>
          </div>
          <div className="inline-flex overflow-hidden rounded-full ring-1 ring-slate-300">
            {['en', 'es'].map((l) => (
              <button key={l} onClick={() => setVl(l)} data-testid={`sds-viewer-lang-${l}`}
                className={`px-3 py-1 text-xs font-bold ${vl === l ? 'bg-[#0E2A47] text-white' : 'bg-white text-slate-600'}`}>{l.toUpperCase()}</button>
            ))}
          </div>
          <a href={available ? fileUrl(row.id, vl, token) : undefined} target="_blank" rel="noreferrer"
            className={`inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold ${available ? 'text-[#1C4FC9]' : 'pointer-events-none opacity-40'}`} data-testid="sds-viewer-open"><ExternalLink size={13} />{TT(vl, 'Open in New Tab', 'Abrir en pestaña')}</a>
          <a href={available ? fileUrl(row.id, vl, token, true) : undefined}
            className={`inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold ${available ? 'text-[#1C4FC9]' : 'pointer-events-none opacity-40'}`} data-testid="sds-viewer-download"><Download size={13} />{TT(vl, 'Download', 'Descargar')}</a>
          <button onClick={onClose} className="inline-flex items-center gap-1 rounded-full bg-[#0E2A47] px-3 py-1 text-xs font-bold text-white" data-testid="sds-viewer-back"><ChevronLeft size={13} />{TT(vl, 'Back', 'Volver')}</button>
        </div>

        {row.pairing_unverified && (
          <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-[11px] text-amber-800" data-testid="sds-viewer-mismatch">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{TT(vl,
              'Pairing not yet verified — the attached manufacturer SDS does not match this product name/code. Confirm from the physical bottle before relying on it.',
              'Emparejamiento sin verificar — la SDS del fabricante adjunta no coincide con el nombre/código de este producto. Confirme desde la botella física antes de utilizarla.')}</span>
          </div>
        )}

        {available && (
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-1.5 text-xs">
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1} data-testid="sds-pdf-prev"
                className="rounded p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40"><ChevronLeft size={16} /></button>
              <span className="tabular-nums text-slate-600" data-testid="sds-pdf-page">{TT(vl, 'Page', 'Página')} {safePage} / {numPages || '…'}</span>
              <button onClick={() => setPage(Math.min(numPages || safePage, safePage + 1))} disabled={!!numPages && safePage >= numPages} data-testid="sds-pdf-next"
                className="rounded p-1 text-slate-600 hover:bg-slate-100 disabled:opacity-40"><ChevronRight size={16} /></button>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setScale((s) => Math.max(0.5, +(s - 0.25).toFixed(2)))} data-testid="sds-pdf-zoomout" className="rounded p-1 text-slate-600 hover:bg-slate-100"><ZoomOut size={16} /></button>
              <span className="tabular-nums text-slate-500">{Math.round(scale * 100)}%</span>
              <button onClick={() => setScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))} data-testid="sds-pdf-zoomin" className="rounded p-1 text-slate-600 hover:bg-slate-100"><ZoomIn size={16} /></button>
            </div>
          </div>
        )}

        <div ref={wrapRef} className="min-h-0 flex-1 overflow-auto bg-slate-300 p-3" data-testid="sds-viewer-frame">
          {!available ? (
            <div className="flex h-full items-center justify-center p-8 text-center text-sm text-slate-500" data-testid="sds-viewer-missing">{TT(vl, 'This language is not uploaded yet for this product.', 'Este idioma aún no se ha cargado para este producto.')}</div>
          ) : err ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center" data-testid="sds-viewer-error">
              <AlertTriangle className="text-rose-500" size={28} />
              <div className="text-sm font-semibold text-rose-700">{TT(vl, 'The document could not be displayed here.', 'No se pudo mostrar el documento aquí.')}</div>
              <div className="text-xs text-slate-600">{TT(vl, 'Use “Open in New Tab” or “Download” above to view the SDS.', 'Use “Abrir en pestaña” o “Descargar” arriba para ver la SDS.')}</div>
            </div>
          ) : (
            <Document file={fileProp} options={docOptions}
              onLoadSuccess={({ numPages: n }) => { setNumPages(n); setErr(false); }}
              onLoadError={() => setErr(true)} onSourceError={() => setErr(true)}
              loading={<div className="flex justify-center py-16 text-slate-500" data-testid="sds-viewer-loading"><Loader2 className="animate-spin" /></div>}
              error={<div className="flex h-full items-center justify-center py-16 text-sm text-rose-600">{TT(vl, 'Could not load the PDF.', 'No se pudo cargar el PDF.')}</div>}>
              <Page pageNumber={safePage} width={width * scale} renderTextLayer={false} renderAnnotationLayer={false}
                className="mx-auto shadow-lg" loading={<div className="flex justify-center py-16 text-slate-400"><Loader2 className="animate-spin" /></div>} />
            </Document>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Admin maintenance for one product ----------------
const AdminPanel = ({ row, token, lang = 'en', onChange }) => {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [enRev, setEnRev] = useState(row.en_revision || '');
  const [esRev, setEsRev] = useState(row.es_revision || '');
  const up = async (kind, l, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const url = kind === 'photo' ? `${API}/api/team/sds/${row.id}/photo` : `${API}/api/team/sds/${row.id}/file?lang=${l}`;
    try { await axios.post(url, fd, h); toast.success(TT('en', 'Uploaded', 'Cargado')); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };
  const saveRev = async () => {
    try { await axios.put(`${API}/api/team/sds/${row.id}`, { en_revision: enRev, es_revision: esRev }, h); toast.success('Saved'); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="mt-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/60 p-3" data-testid={`sds-admin-${row.id}`}>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-amber-800">{TT(lang, 'Administrator · maintain documents', 'Administrador · mantener documentos')}</div>
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600" data-testid={`sds-up-photo-${row.id}`}>
          <ImagePlus size={12} />{row.has_photo ? TT(lang, 'Replace photo', 'Reemplazar foto') : TT(lang, 'Add photo', 'Agregar foto')}<input type="file" accept="image/*" className="hidden" onChange={(e) => up('photo', null, e.target.files[0])} /></label>
        <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600" data-testid={`sds-up-en-${row.id}`}>
          <Upload size={12} />{row.has_en ? TT(lang, 'Replace EN PDF', 'Reemplazar PDF EN') : TT(lang, 'Add EN PDF', 'Agregar PDF EN')}<input type="file" accept="application/pdf" className="hidden" onChange={(e) => up('file', 'en', e.target.files[0])} /></label>
        <label className="inline-flex cursor-pointer items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600" data-testid={`sds-up-es-${row.id}`}>
          <Upload size={12} />{row.has_es ? TT(lang, 'Replace ES PDF', 'Reemplazar PDF ES') : TT(lang, 'Add ES PDF', 'Agregar PDF ES')}<input type="file" accept="application/pdf" className="hidden" onChange={(e) => up('file', 'es', e.target.files[0])} /></label>
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="text-[10px] font-semibold text-slate-500">{TT(lang, 'EN revision', 'Revisión EN')}<input type="date" value={enRev} onChange={(e) => setEnRev(e.target.value)} className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-xs" data-testid={`sds-enrev-${row.id}`} /></label>
        <label className="text-[10px] font-semibold text-slate-500">{TT(lang, 'ES revision', 'Revisión ES')}<input type="date" value={esRev} onChange={(e) => setEsRev(e.target.value)} className="mt-0.5 block rounded border border-slate-300 px-2 py-1 text-xs" data-testid={`sds-esrev-${row.id}`} /></label>
        <button onClick={saveRev} className="rounded-full bg-[#0E2A47] px-3 py-1.5 text-[11px] font-bold text-white" data-testid={`sds-saverev-${row.id}`}>{TT(lang, 'Save dates', 'Guardar fechas')}</button>
      </div>
    </div>
  );
};

// ---------------- Product card ----------------
const ProductCard = ({ row, lang, token, admin, onOpen, onChange }) => {
  const otherL = lang === 'es' ? 'en' : 'es';
  const rev = lang === 'es' ? row.es_revision : row.en_revision;
  const otherRev = lang === 'es' ? row.en_revision : row.es_revision;
  const blocked = row.verification_status === 'verification_required';
  const [showAdmin, setShowAdmin] = useState(false);
  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={`sds-card-${row.id}`}>
      <button onClick={() => { if (!blocked) onOpen(row, lang); }} className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-slate-100" data-testid={`sds-photo-${row.id}`}>
        {row.has_photo
          ? <img src={photoUrl(row.id, token, row.photo_version)} alt={row.product_name} width="512" height="384"
              loading="eager" fetchpriority="high" decoding="async"
              className="h-full w-full object-contain" data-testid={`sds-photo-img-${row.id}`} />
          : <div className="flex flex-col items-center gap-1 text-slate-400"><FolderClosed size={40} /><span className="text-[11px] font-semibold">{TT(lang, 'Photo pending', 'Foto pendiente')}</span></div>}
        {blocked ? (
          <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-[#B91C1C] px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow" data-testid={`sds-verification-badge-${row.id}`}>
            <AlertTriangle size={10} />{TT(lang, 'Verification Required', 'Verificación requerida')}
          </span>
        ) : row.pairing_unverified && (
          <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow" data-testid={`sds-mismatch-badge-${row.id}`}>
            <AlertTriangle size={10} />{TT(lang, 'Verify pairing', 'Verificar')}
          </span>
        )}
      </button>
      <div className="flex flex-1 flex-col p-3">
        <div className="text-sm font-extrabold leading-tight text-[#0E2A47]" data-testid={`sds-name-${row.id}`}>{primaryName(row.product_name)}</div>
        <div className="text-[11px] font-semibold text-slate-400" data-testid={`sds-classification-${row.id}`}>{CLASSIFICATION}</div>
        <div className="text-[11px] font-semibold text-slate-500" data-testid={`sds-code-${row.id}`}>#{row.product_code}</div>
        <div className="mt-1 space-y-0.5 text-[10.5px] text-slate-500">
          {row.en_revision && <div data-testid={`sds-enrevdisp-${row.id}`}>{TT(lang, 'English revision', 'Revisión inglés')}: {fmtRev(row.en_revision, lang)}</div>}
          {row.es_revision && <div data-testid={`sds-esrevdisp-${row.id}`}>{TT(lang, 'Spanish revision', 'Revisión español')}: {fmtRev(row.es_revision, lang)}</div>}
        </div>
        <div className="mt-auto pt-3" data-testid={`sds-actions-${row.id}`}>
          {row.verification_status === 'verification_required' ? (
            <div className="rounded-lg border-2 border-[#B91C1C] bg-rose-50 px-3 py-2.5 text-center" data-testid={`sds-verification-required-${row.id}`}>
              <div className="inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wide text-[#B91C1C]">
                <AlertTriangle size={14} />{TT(lang, 'Verification Required', 'Verificación requerida')}
              </div>
              <div className="mt-1 text-[10.5px] leading-snug text-rose-700">
                {TT(lang,
                  'No confirmed manufacturer SDS is on file for this product. The previously attached sheet did not match and has been withheld. Use the printed yellow SDS binder on-site until a verified sheet is loaded.',
                  'No hay una SDS del fabricante confirmada para este producto. La hoja adjunta anterior no coincidía y ha sido retenida. Use la carpeta amarilla de SDS impresa en el sitio hasta que se cargue una hoja verificada.')}
              </div>
            </div>
          ) : (
            <>
              <button onClick={() => onOpen(row, lang)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[#B91C1C] px-3 py-2 text-xs font-bold text-white" data-testid={`sds-view-${row.id}`}>
                <FileText size={14} />{TT(lang, 'View SDS', 'Ver SDS')} · {lang.toUpperCase()}
              </button>
              <button onClick={() => onOpen(row, otherL)}
                className="mt-1.5 flex min-h-[34px] w-full items-start justify-center text-center text-[11px] font-semibold leading-tight text-[#1C4FC9] underline" data-testid={`sds-view-other-${row.id}`}>
                {TT(lang, 'View in Spanish', 'Ver en inglés')}{otherRev ? ` (${fmtRev(otherRev, lang)})` : ''}
              </button>
            </>
          )}
          {admin && (
            <>
              <button onClick={() => setShowAdmin((s) => !s)} className="mt-2 w-full text-left text-[10px] font-semibold uppercase tracking-wide text-amber-700" data-testid={`sds-admin-toggle-${row.id}`}>{showAdmin ? TT(lang, '− Hide admin', '− Ocultar admin') : TT(lang, '+ Admin Maintenance', '+ Mantenimiento administrativo')}</button>
              {showAdmin && <AdminPanel row={row} token={token} lang={lang} onChange={onChange} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TeamSDS = ({ lang = 'en', token: tokenProp, onBack, backLabel }) => {
  const ctx = useContext(AuthContext);
  const token = tokenProp || ctx?.token;
  const [data, setData] = useState(null);
  const [viewer, setViewer] = useState(null);
  const load = () => axios.get(`${API}/api/team/sds`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setData(r.data)).catch(() => setData({ sds: [], can_manage: false }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (data === null) return <div className="p-10 text-center text-slate-500"><Loader2 className="inline animate-spin" size={18} /></div>;
  const admin = data.can_manage;
  return (
    <div className="mx-auto max-w-4xl px-5 py-6" data-testid="team-sds-page">
      {onBack && <button onClick={onBack} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1C4FC9]" data-testid="team-sds-back"><ChevronLeft size={16} />{backLabel || TT(lang, 'Back to TEAM', 'Volver a TEAM')}</button>}
      <div className="mb-4 flex items-center gap-3 rounded-2xl border-2 border-[#B91C1C] bg-[#FFD400] px-4 py-3" data-testid="team-sds-header">
        <FolderClosed size={32} className="shrink-0 text-[#B91C1C]" />
        <div>
          <h1 className="text-xl font-extrabold leading-none text-[#B91C1C]" data-testid="team-sds-title">{TT(lang, 'Safety Data Sheets', 'Hojas de Datos de Seguridad')}</h1>
          <p className="text-sm font-bold text-[#B91C1C]">{TT(lang, 'Hojas de Datos de Seguridad', 'Safety Data Sheets')}</p>
        </div>
      </div>
      <p className="mb-4 text-[12px] text-slate-500" data-testid="team-sds-intro">
        {TT(lang, 'Chemical Safety reference — available to every team member without training or an assessment. Tap a product to open its manufacturer SDS in English or Español.',
          'Referencia de Seguridad Química — disponible para todos los miembros del equipo sin capacitación ni evaluación. Toque un producto para abrir su SDS del fabricante en inglés o español.')}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="team-sds-grid">
        {data.sds.map((row) => (
          <ProductCard key={row.id} row={row} lang={lang} token={token} admin={admin} onOpen={(r, l) => setViewer({ row: r, lang: l })} onChange={load} />
        ))}
      </div>
      <div className="mt-4 flex items-start gap-1.5 rounded-lg bg-slate-100 p-2 text-[11px] text-slate-500" data-testid="team-sds-offline">
        <WifiOff size={13} className="mt-0.5 shrink-0" />
        {TT(lang, 'If TEAM or the internet is unavailable during a shift, use the printed yellow SDS binder kept on-site at each property.',
          'Si TEAM o el internet no están disponibles durante un turno, use la carpeta amarilla impresa de SDS que se mantiene en cada propiedad.')}
      </div>
      {viewer && <Viewer row={viewer.row} lang={viewer.lang} token={token} onClose={() => setViewer(null)} />}
    </div>
  );
};

export default TeamSDS;
